import PDFExporter from './exporters/PDFExporter';
import ExcelExporter from './exporters/ExcelExporter';
import FinanciamientoExportStrategy from './strategies/FinanciamientoExportStrategy';
import ANRExportStrategy from './strategies/ANRExportStrategy';
import InfoMoraExportStrategy from './strategies/InfoMoraExportStrategy';
import InfoCobranzasProyectadasExportStrategy from './strategies/InfoCobranzasProyectadasExportStrategy';
import InfoCobranzasExportStrategy from './strategies/InfoCobranzasExportStrategy';
import InfoCteYNoCteExportStrategy from './strategies/InfoCteYNoCteExportStrategy';
import SaldosHTCExportStrategy from './strategies/SaldosHTCExportStrategy';

/**
 * Mixin para exportación de reportes usando arquitectura modular
 *
 * Este mixin orquesta la exportación de reportes a PDF y Excel
 * usando el patrón Strategy para mantener cada reporte aislado.
 *
 * USO:
 *
 * 1. Importar en el componente:
 *    import ReportExportMixin from './ReportExport/ReportExportMixin';
 *
 * 2. Agregar al componente:
 *    mixins: [ReportExportMixin]
 *
 * 3. El componente debe tener estas props/data:
 *    - resultados: Array de datos del reporte
 *    - esOperacion: String con el tipo de operación ('Financiamiento', 'ANR', etc.)
 *    - fechaReporte: String con la fecha del reporte
 *    - maxDesembolsos: Number (opcional) cantidad máxima de desembolsos
 *
 * 4. Exponer métodos:
 *    - exportarPDF()
 *    - exportarExcel()
 */
export default {
  methods: {
    /**
     * Exporta el reporte a PDF usando la nueva arquitectura
     * @param {Array} datos - (Opcional) Datos a exportar. Si no se proporciona, usa this.resultados
     */
    async exportarPDF(datos = null) {
      // Usar datos proporcionados o this.resultados
      const datosAExportar = datos || this.resultados;

      if (!datosAExportar || datosAExportar.length === 0) {
        console.warn('No hay datos para exportar');
        return;
      }

      try {
        console.log('📊 Nueva arquitectura - Exportando PDF con', datosAExportar.length, 'registros');

        // Solo emitir evento si NO se pasaron datos (llamada directa desde botón)
        // Si se pasaron datos, el padre ya está manejando la exportación
        if (!datos) {
          this.$emit('exportar-datos', 'pdf');
        }

        // Obtener la estrategia según el tipo de reporte
        const strategy = this.getExportStrategy();

        // Crear el contexto con helpers y configuración (pasando los datos para calcular maxDesembolsos)
        const context = this.createExportContext(datosAExportar);

        // Crear el exportador y generar el PDF
        const exporter = new PDFExporter(strategy, datosAExportar, context);
        exporter.generate();

        console.log('✅ PDF generado exitosamente');
      } catch (error) {
        console.error('❌ Error al generar PDF:', error);
        // Emitir evento de error si es necesario
        this.$emit('error-exportacion', { tipo: 'pdf', error });
        throw error; // Re-lanzar para que el padre lo maneje
      }
    },

    /**
     * Exporta el reporte a Excel usando la nueva arquitectura
     * @param {Array} datos - (Opcional) Datos a exportar. Si no se proporciona, usa this.resultados
     */
    async exportarExcel(datos = null) {
      // Usar datos proporcionados o this.resultados
      const datosAExportar = datos || this.resultados;

      if (!datosAExportar || datosAExportar.length === 0) {
        console.warn('No hay datos para exportar');
        return;
      }

      try {
        console.log('📊 Nueva arquitectura - Exportando Excel con', datosAExportar.length, 'registros');

        // Solo emitir evento si NO se pasaron datos (llamada directa desde botón)
        // Si se pasaron datos, el padre ya está manejando la exportación
        if (!datos) {
          this.$emit('exportar-datos', 'excel');
        }

        // Obtener la estrategia según el tipo de reporte
        const strategy = this.getExportStrategy();

        // Crear el contexto con helpers y configuración (pasando los datos para calcular maxDesembolsos)
        const context = this.createExportContext(datosAExportar);

  
        // Crear el exportador y generar el Excel
        const exporter = new ExcelExporter(strategy, datosAExportar, context);
        exporter.generate();

        console.log('✅ Excel generado exitosamente');
      } catch (error) {
        console.error('❌ Error al generar Excel:', error);
        // Emitir evento de error si es necesario
        this.$emit('error-exportacion', { tipo: 'excel', error });
        throw error; // Re-lanzar para que el padre lo maneje
      }
    },

    /**
     * Obtiene la estrategia de exportación según el tipo de reporte
     * @returns {BaseExportStrategy}
     */
    getExportStrategy() {
      const tipoReporte = this.esOperacion || 'Financiamiento';

      console.log('📊 Seleccionando estrategia para:', tipoReporte);

      // Mapeo de tipos de reporte a estrategias
      switch (tipoReporte) {
        case 'ANR':
        case 'Anr':
          console.log('✅ Usando estrategia de ANR');
          return new ANRExportStrategy();

        case 'Info Mora (Financiamiento)':
          console.log('✅ Usando estrategia de Info Mora (Financiamiento)');
          return new InfoMoraExportStrategy();

        case 'Info Cobranzas Proyectadas':
          console.log('✅ Usando estrategia de Info Cobranzas Proyectadas');
          return new InfoCobranzasProyectadasExportStrategy();

        case 'Info Cobranzas':
          console.log('✅ Usando estrategia de Info Cobranzas');
          return new InfoCobranzasExportStrategy();

        case 'Info Cte y No Cte':
          console.log('✅ Usando estrategia de Info Cte y No Cte');
          return new InfoCteYNoCteExportStrategy();

        case 'Reporte de Saldos - HTC':
          console.log('✅ Usando estrategia de Saldos HTC');
          return new SaldosHTCExportStrategy();

        case 'Financiamiento':
          console.log('✅ Usando estrategia de Financiamiento');
          return new FinanciamientoExportStrategy();

        default:
          console.warn(`⚠️ No hay estrategia específica para ${tipoReporte}, usando Financiamiento por defecto`);
          return new FinanciamientoExportStrategy();
      }
    },

    /**
     * Crea el contexto de exportación con helpers y configuración
     * @param {Array} datos - Datos que se van a exportar (para calcular maxDesembolsos correctamente)
     * @returns {Object}
     */
    createExportContext(datos = null) {
      // Calcular maxDesembolsos sobre los datos que realmente se van a exportar
      const datosParaCalculo = datos || this.resultados || [];
      const maxDesembolsos = this.calcularMaxDesembolsosConDatos(datosParaCalculo);

      const context = {
        // Tipo de operación
        esOperacion: this.esOperacion || 'Financiamiento',

        // Fecha del reporte
        fechaReporte: this.fechaReporte || new Date().toISOString().substr(0, 10),

        // Cantidad máxima de desembolsos (calculada sobre los datos a exportar)
        maxDesembolsos: maxDesembolsos,

        // Helper: Formato de números
        formatoNumero: (numero) => {
          if (numero === null || numero === undefined) return '0,00';
          const num = parseFloat(numero);
          if (isNaN(num)) return '0,00';
          return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        },

        // Helper: Obtener tipo de monto
        getTipoMonto: (parametros, tipoMonto) => {
          if (!parametros) return 0;
          const valor = parametros[tipoMonto];
          if (!valor) return 0;
          if (Array.isArray(valor)) {
            return parseFloat(valor[0]) || 0;
          }
          return parseFloat(valor) || 0;
        },

        // Helper: Obtener desembolso por número
        getDesembolso: (item, num) => {
          if (!item.desembolsos || !Array.isArray(item.desembolsos)) {
            return null;
          }
          return item.desembolsos.find(d => d.numeroDesembolso === num) || null;
        },

        // Helper: Calcular monto pendiente
        getPendienteDesembolso: (item) => {
          const montoAprobado = this.getTipoMontoHelper(item.parametros, 'monto_aprobado') || item.monto_aprobado || 0;

          // Solo validar estado para Financiamiento normal (no ANR, no reportes especiales)
          const debeValidarEstado = this.esOperacion === 'Financiamiento';



          let montoOtorgado = 0;
          if (!debeValidarEstado || this.esEstadoValidoParaMontoHelper(item)) {
            montoOtorgado = this.getTipoMontoHelper(item.parametros, 'monto') || 0;
          }

          return montoAprobado - montoOtorgado;
        },

        // Referencia a métodos del mixin si son necesarios
        // (Por ejemplo, si algunas estrategias necesitan acceso a computed del componente)
      };

      // Para Info Cobranzas Proyectadas, calcular columnas de meses
      if (this.esOperacion === 'Info Cobranzas Proyectadas') {
        context.columnasMeses = this.calcularColumnasMesesParaExport(datosParaCalculo);
        context.datos = datosParaCalculo;
        console.log(`📊 Info Cobranzas Proyectadas - Columnas de meses calculadas: ${context.columnasMeses.length}`);
      }

      // Para Info Cobranzas, calcular columnas de períodos
      if (this.esOperacion === 'Info Cobranzas') {
        context.columnasPeriodos = this.calcularColumnasPeriodosParaExport(datosParaCalculo);
        context.datos = datosParaCalculo;
        console.log(`📊 Info Cobranzas - Columnas de períodos calculadas: ${context.columnasPeriodos.length}`);
      }

      // Para Reporte de Saldos - HTC, agregar periodo al contexto
      if (this.esOperacion === 'Reporte de Saldos - HTC' && this.periodoSaldosHtc) {
        context.periodoSaldosHtc = this.periodoSaldosHtc;
        console.log(`📊 Saldos HTC - Periodo agregado al contexto: ${this.periodoSaldosHtc}`);
      }

      return context;
    },

    /**
     * Helper interno para getTipoMonto
     */
    getTipoMontoHelper(parametros, tipoMonto) {
      if (!parametros) return 0;
      const valor = parametros[tipoMonto];
      if (!valor) return 0;
      if (Array.isArray(valor)) {
        return parseFloat(valor[0]) || 0;
      }
      return parseFloat(valor) || 0;
    },

    /**
     * Helper interno para validar si un estado permite mostrar monto otorgado
     * Solo los estados "Otorgado" y "Cancelado" tienen monto otorgado > 0
     */
    esEstadoValidoParaMontoHelper(item) {
      if (!item || !item.estado) return false;
      const estadosValidos = ['Otorgado', 'Cancelado'];
      return estadosValidos.includes(item.estado);
    },

    /**
     * Calcula el número máximo de desembolsos que tienen datos reales
     * @param {Array} datos - Datos sobre los cuales calcular
     * @returns {Number}
     */
    calcularMaxDesembolsosConDatos(datos) {
      if (!datos || datos.length === 0) return 0;

      let maxConDatos = 0;

      // Recorrer todos los items para encontrar el desembolso más alto que tenga datos
      datos.forEach(item => {
        if (item._esSubtotal || !item.desembolsos || !Array.isArray(item.desembolsos)) return;

        // Para cada desembolso, verificar si tiene datos reales (monto > 0 o fecha)
        item.desembolsos.forEach(desembolso => {
          const tieneMonto = desembolso.monto && parseFloat(desembolso.monto) > 0;
          const tieneFecha = desembolso.fecha_desembolso && desembolso.fecha_desembolso !== '-';

          if (tieneMonto || tieneFecha) {
            const numDesembolso = desembolso.numeroDesembolso || 0;
            maxConDatos = Math.max(maxConDatos, numDesembolso);
          }
        });
      });

      console.log(`📊 Desembolsos con datos reales (calculado sobre ${datos.length} registros): ${maxConDatos}`);
      return maxConDatos;
    },

    /**
     * Calcula las columnas de meses para Info Cobranzas Proyectadas
     * Los datos ya vienen procesados con propiedades fecha_MES_AÑO
     * @param {Array} datos - Datos del reporte
     * @returns {Array}
     */
    calcularColumnasMesesParaExport(datos) {
      const meses = {
        1: 'ene', 2: 'feb', 3: 'mar', 4: 'abr', 5: 'may', 6: 'jun',
        7: 'jul', 8: 'ago', 9: 'sep', 10: 'oct', 11: 'nov', 12: 'dic'
      };

      const periodosConAño = new Map();

      // Detectar períodos desde las propiedades procesadas fecha_MES_AÑO
      datos.forEach(item => {
        if (!item._esSubtotal) {
          // Buscar todas las propiedades que empiecen con "fecha_" y terminen con "_YYYY"
          Object.keys(item).forEach(key => {
            if (key.startsWith('fecha_') && item[key] && item[key] > 0) {
              // Extraer mes y año del formato fecha_MES_AÑO
              const partes = key.split('_');
              if (partes.length === 3) {
                const mes = parseInt(partes[1], 10);
                const año = parseInt(partes[2], 10);

                if (mes >= 1 && mes <= 12 && año > 2000) {
                  // Crear clave única por mes-año para ordenamiento
                  const clave = `${año}-${mes.toString().padStart(2, '0')}`;
                  periodosConAño.set(clave, { mes, año });
                }
              }
            }
          });
        }
      });

      const fechasHeaders = [];

      // Generar headers ordenados cronológicamente
      if (periodosConAño.size > 0) {
        Array.from(periodosConAño.keys())
          .sort()
          .forEach(clave => {
            const { mes, año } = periodosConAño.get(clave);
            const añoCorto = año.toString().slice(-2);
            fechasHeaders.push({
              text: `${meses[mes]} ${añoCorto}`,
              value: `fecha_${mes}_${año}`
            });
          });
      }

      console.log(`📊 Info Cobranzas Proyectadas - Calculadas ${fechasHeaders.length} columnas de meses para exportación`);
      return fechasHeaders;
    },

    /**
     * Calcula las columnas de períodos para Info Cobranzas
     * Los datos ya vienen procesados con propiedades periodo_YYYY-MM
     * @param {Array} datos - Datos del reporte
     * @returns {Array}
     */
    calcularColumnasPeriodosParaExport(datos) {
      const meses = {
        1: 'ene', 2: 'feb', 3: 'mar', 4: 'abr', 5: 'may', 6: 'jun',
        7: 'jul', 8: 'ago', 9: 'sep', 10: 'oct', 11: 'nov', 12: 'dic'
      };

      const periodosSet = new Set();

      // Detectar períodos desde las propiedades procesadas periodo_YYYY-MM
      datos.forEach(item => {
        if (!item._esSubtotal) {
          // Buscar todas las propiedades que empiecen con "periodo_"
          Object.keys(item).forEach(key => {
            if (key.startsWith('periodo_') && item[key] && item[key] > 0) {
              // Extraer el período YYYY-MM de la clave periodo_YYYY-MM
              const periodo = key.replace('periodo_', '');
              periodosSet.add(periodo);
            }
          });
        }
      });

      const periodosHeaders = [];

      // Generar headers ordenados cronológicamente
      if (periodosSet.size > 0) {
        Array.from(periodosSet)
          .sort()
          .forEach(periodo => {
            // periodo: "2025-11"
            const [año, mesStr] = periodo.split('-');
            const mesNum = parseInt(mesStr, 10);
            const añoCorto = año.slice(-2);

            periodosHeaders.push({
              text: `${meses[mesNum]} ${añoCorto}`,
              value: `periodo_${periodo}`,
              periodo: periodo // Guardar el período para búsquedas
            });
          });
      }

      console.log(`📊 Info Cobranzas - Calculadas ${periodosHeaders.length} columnas de períodos para exportación`);
      return periodosHeaders;
    },
  },
};
